/**
 * The blob maker: name, body shape, colour, tone and hat, with a live preview.
 * Used for first arrival and for the wardrobe later.
 */
import { HATS, HUE_COUNT, HUE_STEP, randomLook, SHAPES, TONES, type Look } from '../../../shared/cosmetics.ts'
import { cleanName, NAME_MAX } from '../../../shared/moderation.ts'
import { EMOTES } from '../../../shared/protocol.ts'
import { blobArt, blobMotion, drawBlob } from '../render/blob.ts'
import { BRAND, TAGLINE } from '../brand.ts'
import { h, ICON } from './dom.ts'
import { thumb } from './thumb.ts'

const FIRST = ['Mochi', 'Pebble', 'Dumpling', 'Biscuit', 'Bean', 'Noodle', 'Puddle', 'Tofu', 'Marble', 'Sprout', 'Gumdrop', 'Pip', 'Waffle', 'Bubbles', 'Taro', 'Nimbus', 'Pudding', 'Clover', 'Button', 'Jellybean']
const SECOND = ['', '', '', 'Jr', 'Bun', 'Pie', 'Puff', 'Boo', 'Bop']
export function randomName(): string {
  const a = FIRST[Math.floor(Math.random() * FIRST.length)]!
  const b = SECOND[Math.floor(Math.random() * SECOND.length)]!
  return b ? `${a} ${b}` : a
}

export interface MakerResult { name: string; look: Look }

/** Swatch colour for a hue and tone, read off blobatar itself so it always matches. */
function swatch(hue: number, tone: number): string {
  return blobArt({ seed: 'swatch', shape: 0, hue, tone, hat: 'none' }).color
}

export function openMaker(initial: MakerResult, opts: { first: boolean; onDone: (r: MakerResult) => void; onCancel?: () => void }): void {
  let look = { ...initial.look }
  const nameIn = h('input', { value: initial.name, maxlength: NAME_MAX, 'aria-label': 'Your name', autocomplete: 'off', spellcheck: 'false' })
  const err = h('div', { class: 'err', role: 'status' })
  const preview = h('canvas', { 'aria-label': 'Preview of your blob', role: 'img' })
  let expr = 0
  preview.addEventListener('click', () => { expr = (expr + 1) % (EMOTES.length + 1); poke = performance.now() })
  let poke = 0

  const shapeGrid = h('div', { class: 'grid', role: 'group', 'aria-label': 'Body shape' })
  const hueRow = h('div', { class: 'hues', role: 'group', 'aria-label': 'Colour' })
  const toneRow = h('div', { class: 'tones', role: 'group', 'aria-label': 'Tone' })
  const hatGrid = h('div', { class: 'grid', role: 'group', 'aria-label': 'Hat' })

  function renderPickers(): void {
    shapeGrid.replaceChildren(...SHAPES.map((s, i) => {
      const b = h('button', { 'aria-pressed': String(look.shape === i), title: s.label, 'aria-label': s.label, onclick: () => { look.shape = i; renderPickers() } })
      b.append(thumb({ ...look, shape: i }, 46, 'idle', false))
      return b
    }))
    hueRow.replaceChildren(...Array.from({ length: HUE_COUNT }, (_, i) => h('button', {
      'aria-pressed': String(look.hue === i), 'aria-label': `Hue ${i * HUE_STEP}°`, style: `background:${swatch(i, look.tone)}`,
      onclick: () => { look.hue = i; renderPickers() },
    })))
    toneRow.replaceChildren(...TONES.map((tn, i) => {
      const c = swatch(look.hue, i)
      return h('button', { 'aria-pressed': String(look.tone === i), style: `background:${c};color:${i === TONES.length - 1 || i === 3 ? '#fff' : '#3a3552'}`, onclick: () => { look.tone = i; renderPickers() } }, tn.label)
    }))
    hatGrid.replaceChildren(...HATS.map((hat) => {
      const b = h('button', { 'aria-pressed': String(look.hat === hat.id), title: hat.label, 'aria-label': hat.label, onclick: () => { look.hat = hat.id; renderPickers() } })
      b.append(thumb({ ...look, hat: hat.id }, 46))
      return b
    }))
  }
  renderPickers()

  const shuffle = h('button', { class: 'btn', onclick: () => { look = randomLook(); renderPickers() } })
  shuffle.innerHTML = `${ICON.dice}<span>Shuffle</span>`
  const reroll = h('button', { class: 'btn', title: 'New eyes', onclick: () => { look = { ...look, seed: randomLook().seed }; renderPickers() } })
  reroll.innerHTML = `${ICON.eye}<span>New face</span>`
  const nameDice = h('button', { class: 'btn', title: 'Random name', onclick: () => { nameIn.value = randomName(); err.textContent = '' } })
  nameDice.innerHTML = `${ICON.dice}<span>Name</span>`

  const done = h('button', { class: 'btn primary' }, opts.first ? `Hop into ${BRAND}` : 'Save')
  const cancel = opts.first ? null : h('button', { class: 'btn ghost' }, 'Cancel')

  const dialog = h('div', { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'maker-title' },
    h('h1', { id: 'maker-title' }, opts.first ? `Welcome to ${BRAND}` : 'Wardrobe'),
    h('p', { class: 'sub' }, opts.first ? `${TAGLINE} Make your blob, then go say hi.` : 'Change anything. Everyone sees it right away.'),
    h('div', { class: 'maker' },
      h('div', { class: 'preview' }, preview, nameIn, err, h('div', { class: 'row2' }, nameDice, reroll, shuffle)),
      h('div', { class: 'pick' },
        h('h3', {}, 'Shape'), shapeGrid,
        h('h3', {}, 'Colour'), hueRow, toneRow,
        h('h3', {}, 'Hat'), hatGrid,
      ),
    ),
    h('div', { class: 'foot' }, cancel, done),
  )
  const scrim = h('div', { class: 'scrim' }, dialog)
  document.body.append(scrim)
  if (!opts.first) nameIn.select()

  let raf = 0
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches
  const loop = (now: number) => {
    const c = preview
    const px = c.clientWidth || 220
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    if (c.width !== Math.round(px * dpr)) { c.width = Math.round(px * dpr); c.height = c.width }
    const ctx = c.getContext('2d')!
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, c.width, c.height)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    const t = now / 1000
    const since = (now - poke) / 800
    const m = blobMotion('stand', t, 0, since < 1 ? { a: 'hop', k: since } : undefined, calm)
    ctx.fillStyle = 'rgba(40,28,64,0.14)'
    ctx.beginPath(); ctx.ellipse(px / 2, px * 0.86, px * 0.2, px * 0.05, 0, 0, Math.PI * 2); ctx.fill()
    const emote = expr === 0 ? 'idle' : EMOTES[expr - 1]!
    drawBlob(ctx, blobArt(look, emote), px / 2, px * 0.86, { size: px * 0.78, facing: 1, sx: m.sx, sy: m.sy, rot: m.rot, dyExtra: m.dy, eyes: m.eyes, hat: look.hat, t })
    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)

  const close = () => { cancelAnimationFrame(raf); scrim.remove(); document.removeEventListener('keydown', onKey) }
  const submit = () => {
    const name = cleanName(nameIn.value)
    if (!name) {
      err.textContent = nameIn.value.trim().length < 2 ? 'Pick a name with at least 2 letters.' : 'That name can’t be used. Try another.'
      nameIn.focus()
      return
    }
    close()
    opts.onDone({ name, look: { ...look } })
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !opts.first) { close(); opts.onCancel?.() }
    if (e.key === 'Enter' && document.activeElement === nameIn) submit()
  }
  document.addEventListener('keydown', onKey)
  done.addEventListener('click', submit)
  cancel?.addEventListener('click', () => { close(); opts.onCancel?.() })
  scrim.addEventListener('pointerdown', (e) => { if (e.target === scrim && !opts.first) { close(); opts.onCancel?.() } })
}
